/**
 * GPU-accelerated brush-bounded Gaussian smoothing of an SDF.
 *
 * One brush pass computes, for every voxel p inside the brush ball,
 *
 *   sdf'(p) = mix(sdf(p), gauss(sdf)(p), w(p))
 *
 * where gauss is a separable Gaussian blur (X, Y, Z passes) and w is a
 * C¹ radial falloff of the patient-mm distance r from the cursor:
 * w = 1 for r ≤ R/2, smoothstep to 0 at the brush radius R. Outside
 * the ball w = 0 and nothing is written. Repeated passes while the
 * user drags accumulate, giving the progressive local smoothing feel.
 *
 * Distances are evaluated in patient mm so the kernel is correct on
 * anisotropic grids: per-axis voxel sigma = sigmaMm / res[axis], the
 * same convention as the whole-structure GL smoothing.
 *
 * The Z pass deliberately caps its kernel at ±{@link MAX_KERNEL_RADIUS_Z}
 * slices: the display SDF has no metric propagation along Z (see
 * docs/sdf-model.md) and a per-frame reinitialization is not feasible
 * inside a rAF budget, so cross-slice mixing is kept short-range where
 * the neighbouring slices' in-plane-metric values are still meaningful.
 * In 2D mode the Z pass is skipped entirely and w is additionally
 * gated to the cursor's slice, so content never crosses slices.
 *
 * Pass layout per frame (B = write region, the brush ball's voxel
 * bounding box):
 *   1. blur X: live SDF        → scratchA over dilate(B, ±ry, ±rz)
 *   2. blur Y: scratchA        → scratchB over dilate(B, ±rz)
 *   3. blend:  scratchB + live → scratchA over B (short Z blur + mix)
 *   4. copy:   scratchA        → live SDF over B
 * The final blend cannot render straight into the live texture because
 * it also samples it (feedback would be undefined), hence the copy.
 *
 * Runs synchronously within a rAF callback and therefore does NOT
 * acquire GlOperationGuard (same contract as DeformRenderer).
 */

import { mat4, ReadonlyMat4, ReadonlyVec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { StrokeMode } from '~/state/atoms/toolsAtoms';
import {
    buildGaussianKernel,
    GaussianBlurShader,
    KERNEL_BUFFER_LENGTH,
} from './GaussianSmoothingGL';
import { clampBoxToGrid, isEmptyBox, VoxelBox } from './voxelBox';
import { SDF_DEFAULT_MARGIN_MM } from './sdfConstants';
import { resetGlDefaults } from './utils';

/**
 * Brush strength (1-5) to Gaussian sigma in mm applied PER PASS.
 * Explicit mm (not voxels) so the felt effect is spacing-independent;
 * values are smaller than the whole-structure levels because passes
 * accumulate while dragging.
 */
const BRUSH_STRENGTH_TO_SIGMA_MM: Record<number, number> = {
    1: 0.3,
    2: 0.6,
    3: 1.0,
    4: 1.5,
    5: 2.0,
};

/**
 * Sigma in mm for a brush strength (1-5, clamped). Fractional
 * strengths (from the continuous slider) interpolate linearly between
 * the table entries — deterministic and monotone over the whole range.
 */
export function getBrushSigmaMm(strength: number): number {
    const clamped = Math.max(1, Math.min(5, strength));
    const lower = Math.floor(clamped);
    const upper = Math.ceil(clamped);
    const t = clamped - lower;
    return (
        BRUSH_STRENGTH_TO_SIGMA_MM[lower] * (1 - t) +
        BRUSH_STRENGTH_TO_SIGMA_MM[upper] * t
    );
}

/**
 * In-plane kernel radius cap (voxels). Bounds the per-frame tap count
 * on fine grids; the truncated kernel is renormalized so weights
 * always sum to 1.
 */
const MAX_KERNEL_RADIUS_XY = 7;

/**
 * Z kernel radius cap (slices). Deliberately short — see the module
 * doc: the display SDF is only metric in-plane, so cross-slice mixing
 * is limited to immediate neighbours.
 */
const MAX_KERNEL_RADIUS_Z = 2;

/** Uniform array length of {@link SmoothBlendShader}'s `uKernelZ`. */
const Z_KERNEL_BUFFER_LENGTH = 5;

export interface SmoothBrushPassParams {
    /** The structure's live SDF texture (read and, via copy, written). */
    liveTex: WebGLTexture;
    /** Caller-owned full-volume R8 scratch textures (one per stroke). */
    scratchA: WebGLTexture;
    scratchB: WebGLTexture;
    /** Caller-owned FBO sized (nx, ny). */
    fbo: FrameBufferObject;
    /** Texture size in voxels (nx, ny, nz). */
    texSize: ReadonlyVec3;
    /** Voxel spacing in mm (x, y, z). */
    resMmPerVoxel: ReadonlyVec3;
    voxelToWorld: ReadonlyMat4;
    /** Cursor position in patient mm. */
    mouseMm: ReadonlyVec3;
    /** Brush radius R in mm (w reaches 0 here). */
    brushRadiusMm: number;
    /** Gaussian sigma in mm for this pass. */
    sigmaMm: number;
    mode: StrokeMode;
    /** Unit plane normal of the brushed view (patient space). */
    viewDirection: ReadonlyVec3;
    /**
     * Thickness (mm) of the slab, centred on the brushed view's plane,
     * that a 2D pass is confined to.
     */
    slabThicknessMm: number;
    /** Write region B (brush ball bounding box, already clamped). */
    box: VoxelBox;
}

/**
 * Final blend pass: finishes the separable blur with the short Z pass
 * on the XY-blurred field, then mixes it with the untouched SDF by
 * the radial brush weight.
 */
class SmoothBlendShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;
        in vec2 position;
        out vec2 vUV;
        void main() {
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        in vec2 vUV;
        out vec4 fragColor;

        uniform sampler3D uCurrentTex;  // live SDF before this pass
        uniform sampler3D uBlurredTex;  // XY-blurred field
        uniform vec3 uTexSize;          // (nx, ny, nz)
        uniform float uSliceZ;          // voxel-centred Z of the slice
        uniform mat4 uVoxelToWorld;     // voxel -> patient mm
        uniform vec3 uMouseMm;          // cursor (mm)
        uniform float uBrushRadiusMm;   // w reaches 0 here
        uniform int uKernelRadiusZ;     // 0..2
        uniform float uKernelZ[5];      // normalized Z kernel
        uniform float uTexelSizeZ;      // 1 / nz
        // Slice spacing expressed in the SDF's normalized encoding
        // (resZ mm / (2 * falloffMm)) — the Lipschitz band per slice
        // step for the Z-tap clamp below.
        uniform float uZBandPerSlice;
        // 1 when smoothing is confined to the view's current slice.
        uniform int uMode2d;
        // Brushed view's plane normal (unit, patient space).
        uniform vec3 uViewDirection;
        // Thickness (mm) of the slab, centred on the brushed
        // view's plane, that a 2D pass is confined to.
        uniform float uSlabThicknessMm;

        void main() {
            vec3 voxelPos = vec3(vUV * uTexSize.xy, uSliceZ);
            vec3 uvw = voxelPos / uTexSize;
            vec3 pMm = (uVoxelToWorld * vec4(voxelPos, 1.0)).xyz;
            vec3 dMm = pMm - uMouseMm;
            float r = length(dMm);

            // C¹ radial falloff: full effect within half the radius,
            // eased out to 0 at the rim.
            float w = 1.0 -
                smoothstep(0.5 * uBrushRadiusMm, uBrushRadiusMm, r);

            // In 2D mode voxels outside the slab around the
            // cursor's plane keep their value (w = 0).
            if (uMode2d == 1) {
                float offMm = abs(dot(dMm, uViewDirection));
                if (offMm > 0.5 * uSlabThicknessMm) {
                    w = 0.0;
                }
            }

            // Finish the separable blur with the short Z pass. Each
            // off-slice tap is Lipschitz-clamped first: the display SDF
            // has no metric Z propagation, so one slice past a thin
            // structure the field reads saturated far-outside even
            // though the surface is only one slice spacing away — an
            // unclamped 3D pass erodes caps and few-slice structures
            // disproportionately. A true distance field can change by
            // at most the sampled distance |k| slices, so the tap is
            // clamped to that band around the in-slice value.
            float sCenter = texture(uBlurredTex, uvw).r;
            float blurred = 0.0;
            for (int k = -2; k <= 2; k++) {
                if (k < -uKernelRadiusZ || k > uKernelRadiusZ) continue;
                vec3 c = uvw + vec3(0.0, 0.0, uTexelSizeZ * float(k));
                c = clamp(c, vec3(0.0), vec3(1.0));
                float s = texture(uBlurredTex, c).r;
                float band = float(abs(k)) * uZBandPerSlice;
                s = clamp(s, sCenter - band, sCenter + band);
                blurred += s * uKernelZ[k + uKernelRadiusZ];
            }

            float cur = texture(uCurrentTex, uvw).r;
            float v = mix(cur, blurred, w);
            fragColor = vec4(v, v, v, 1.0);
        }`;

    public readonly uTexSize: UniformVec3;
    public readonly uSliceZ: UniformFloat;
    public readonly uVoxelToWorld: UniformMatrix;
    public readonly uMouseMm: UniformVec3;
    public readonly uBrushRadiusMm: UniformFloat;
    public readonly uKernelRadiusZ: UniformInt;
    public readonly uTexelSizeZ: UniformFloat;
    public readonly uZBandPerSlice: UniformFloat;
    public readonly uMode2d: UniformInt;
    public readonly uViewDirection: UniformVec3;
    public readonly uSlabThicknessMm: UniformFloat;

    private readonly kernelZLocation: WebGLUniformLocation | null;

    public constructor() {
        super(
            SmoothBlendShader.VERTEX_CONTENT,
            SmoothBlendShader.FRAGMENT_CONTENT,
            'position'
        );

        this.uTexSize = new UniformVec3('uTexSize');
        this.uSliceZ = new UniformFloat('uSliceZ');
        this.uVoxelToWorld = new UniformMatrix('uVoxelToWorld');
        this.uMouseMm = new UniformVec3('uMouseMm');
        this.uBrushRadiusMm = new UniformFloat('uBrushRadiusMm');
        this.uKernelRadiusZ = new UniformInt('uKernelRadiusZ');
        this.uTexelSizeZ = new UniformFloat('uTexelSizeZ');
        this.uZBandPerSlice = new UniformFloat('uZBandPerSlice');
        this.uMode2d = new UniformInt('uMode2d');
        this.uViewDirection = new UniformVec3('uViewDirection');
        this.uSlabThicknessMm = new UniformFloat('uSlabThicknessMm');

        super.storeUniformLocations(
            this.uTexSize,
            this.uSliceZ,
            this.uVoxelToWorld,
            this.uMouseMm,
            this.uBrushRadiusMm,
            this.uKernelRadiusZ,
            this.uTexelSizeZ,
            this.uZBandPerSlice,
            this.uMode2d,
            this.uViewDirection,
            this.uSlabThicknessMm
        );

        this.kernelZLocation = gl.getUniformLocation(
            this.programID,
            'uKernelZ[0]'
        );

        this.start();
        gl.uniform1i(
            gl.getUniformLocation(this.programID, 'uCurrentTex'),
            0 // texture unit 0
        );
        gl.uniform1i(
            gl.getUniformLocation(this.programID, 'uBlurredTex'),
            1 // texture unit 1
        );
        this.stop();
    }

    public loadKernelZ(kernel: Float32Array): void {
        if (this.kernelZLocation) {
            gl.uniform1fv(this.kernelZLocation, kernel);
        }
    }
}

/**
 * Frame-time renderer for one smoothing-brush pass. Owns the shaders;
 * the scratch textures and FBO are owned by the caller (one set per
 * stroke).
 */
export class SmoothBrushRenderer implements Renderer {
    private readonly blurShader: GaussianBlurShader;
    private readonly blendShader: SmoothBlendShader;

    public constructor() {
        this.blurShader = new GaussianBlurShader();
        this.blendShader = new SmoothBlendShader();
    }

    public delete(): void {
        this.blurShader.delete();
        this.blendShader.delete();
    }

    /** Apply one brush pass (see module doc for the pass layout). */
    public renderBrushPass(params: SmoothBrushPassParams): void {
        const { texSize, resMmPerVoxel, mode } = params;
        const nx = texSize[0];
        const ny = texSize[1];
        const nz = texSize[2];
        if (nx === 0 || ny === 0 || nz === 0) {
            return;
        }

        const box = clampBoxToGrid(params.box, texSize);
        if (isEmptyBox(box)) {
            return;
        }

        const kernelX = buildGaussianKernel(
            params.sigmaMm / resMmPerVoxel[0],
            MAX_KERNEL_RADIUS_XY,
            KERNEL_BUFFER_LENGTH
        );
        const kernelY = buildGaussianKernel(
            params.sigmaMm / resMmPerVoxel[1],
            MAX_KERNEL_RADIUS_XY,
            KERNEL_BUFFER_LENGTH
        );
        // No cross-slice blur at all in 2D mode.
        const kernelZ =
            mode === '2d'
                ? { kernel: Float32Array.from([1]), rad: 0 }
                : buildGaussianKernel(
                      params.sigmaMm / resMmPerVoxel[2],
                      MAX_KERNEL_RADIUS_Z,
                      Z_KERNEL_BUFFER_LENGTH
                  );

        // Blur output regions, dilated so every tap a later pass takes
        // lands on freshly written texels (see module doc).
        const boxX = clampBoxToGrid(
            {
                ...box,
                yMin: box.yMin - kernelY.rad,
                yMax: box.yMax + kernelY.rad,
                zMin: box.zMin - kernelZ.rad,
                zMax: box.zMax + kernelZ.rad,
            },
            texSize
        );
        const boxY = clampBoxToGrid(
            {
                ...box,
                zMin: box.zMin - kernelZ.rad,
                zMax: box.zMax + kernelZ.rad,
            },
            texSize
        );

        // 1. blur X: live -> scratchA
        this.renderBlurPass(
            params.liveTex,
            params.scratchA,
            params.fbo,
            0,
            kernelX,
            boxX,
            nx,
            ny,
            nz
        );
        // 2. blur Y: scratchA -> scratchB
        this.renderBlurPass(
            params.scratchA,
            params.scratchB,
            params.fbo,
            1,
            kernelY,
            boxY,
            nx,
            ny,
            nz
        );
        // 3. blend (short Z blur + radial mix): scratchB + live -> scratchA
        this.renderBlendPass(params, kernelZ, box, nx, ny, nz);
        // 4. copy the blended region back into the live texture.
        this.copyRegionToLive(params, box);

        resetGlDefaults();
    }

    private renderBlurPass(
        srcTex: WebGLTexture,
        dstTex: WebGLTexture,
        fbo: FrameBufferObject,
        axis: number,
        kernel: { kernel: Float32Array; rad: number },
        box: VoxelBox,
        nx: number,
        ny: number,
        nz: number
    ): void {
        this.blurShader.start();
        this.blurShader.loadTexelSize(1 / nx, 1 / ny, 1 / nz);
        this.blurShader.uAxis.loadInt(axis);
        this.blurShader.uKernelRadius.loadInt(kernel.rad);
        this.blurShader.loadKernel(kernel.kernel);

        gl.viewport(0, 0, nx, ny);
        RenderStorage.getQuadVAO().bind(0);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, srcTex);

        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(
            box.xMin,
            box.yMin,
            box.xMax - box.xMin + 1,
            box.yMax - box.yMin + 1
        );
        try {
            for (let z = box.zMin; z <= box.zMax; z++) {
                this.blurShader.uSliceZ.loadFloat((z + 0.5) / nz);
                fbo.bind();
                fbo.createTextureLayer(dstTex, z);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        } finally {
            gl.disable(gl.SCISSOR_TEST);
        }

        RenderStorage.getQuadVAO().unbind(0);
        this.blurShader.stop();
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    private renderBlendPass(
        params: SmoothBrushPassParams,
        kernelZ: { kernel: Float32Array; rad: number },
        box: VoxelBox,
        nx: number,
        ny: number,
        nz: number
    ): void {
        const shader = this.blendShader;
        shader.start();
        shader.uTexSize.loadVec3(nx, ny, nz);
        // setValue copies into an internal Float32Array, so casting
        // away the ReadonlyMat4 marker is safe.
        shader.uVoxelToWorld.loadMatrix(params.voxelToWorld as mat4);
        shader.uMouseMm.loadVec3(params.mouseMm);
        shader.uBrushRadiusMm.loadFloat(params.brushRadiusMm);
        shader.uKernelRadiusZ.loadInt(kernelZ.rad);
        shader.loadKernelZ(kernelZ.kernel);
        shader.uTexelSizeZ.loadFloat(1 / nz);
        // Slice spacing in the quantized SDF's normalized value scale:
        // encoding maps [-falloff, +falloff] mm onto [0, 1].
        shader.uZBandPerSlice.loadFloat(
            params.resMmPerVoxel[2] / (2 * SDF_DEFAULT_MARGIN_MM)
        );
        shader.uMode2d.loadInt(params.mode === '2d' ? 1 : 0);
        shader.uViewDirection.loadVec3(params.viewDirection);
        shader.uSlabThicknessMm.loadFloat(params.slabThicknessMm);

        gl.viewport(0, 0, nx, ny);
        RenderStorage.getQuadVAO().bind(0);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, params.liveTex);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, params.scratchB);

        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(
            box.xMin,
            box.yMin,
            box.xMax - box.xMin + 1,
            box.yMax - box.yMin + 1
        );
        try {
            for (let z = box.zMin; z <= box.zMax; z++) {
                shader.uSliceZ.loadFloat(z + 0.5);
                params.fbo.bind();
                params.fbo.createTextureLayer(params.scratchA, z);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        } finally {
            gl.disable(gl.SCISSOR_TEST);
        }

        RenderStorage.getQuadVAO().unbind(0);
        shader.stop();
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, null);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    /**
     * Copy the blended region from scratchA into the live texture.
     * copyTexSubImage3D reads from the framebuffer scratchA is
     * attached to, so no draw call (and no feedback loop) is needed.
     */
    private copyRegionToLive(
        params: SmoothBrushPassParams,
        box: VoxelBox
    ): void {
        const width = box.xMax - box.xMin + 1;
        const height = box.yMax - box.yMin + 1;
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, params.liveTex);
        try {
            for (let z = box.zMin; z <= box.zMax; z++) {
                params.fbo.bind();
                params.fbo.createTextureLayer(params.scratchA, z);
                gl.copyTexSubImage3D(
                    gl.TEXTURE_3D,
                    0,
                    box.xMin,
                    box.yMin,
                    z,
                    box.xMin,
                    box.yMin,
                    width,
                    height
                );
            }
        } finally {
            gl.bindTexture(gl.TEXTURE_3D, null);
            params.fbo.unbind();
        }
    }
}
